import type { AssignmentType, UserOption } from './AssignmentGroupForm';
import type { ChecklistAssignmentGroup } from './CheckListAndToDoGroups';

const parseIdFromIri = (iri?: string): string | null => {
    if (!iri || typeof iri !== 'string') return null;
    const parts = iri.split('/').filter(Boolean);
    return parts[parts.length - 1] ?? null;
};

export const extractGroupMembers = (group: any): any[] => {
    if (Array.isArray(group?.checklistGroupMembers)) return group.checklistGroupMembers;
    if (Array.isArray(group?.checklist_group_members)) return group.checklist_group_members;
    return [];
};

export const groupNeedsMemberDetail = (group: any): boolean => {
    const members = extractGroupMembers(group);
    const type = group?.type ?? group?.assignmentType;
    const isUsers = type === 'users' || type === 'user';
    return isUsers && members.length === 0;
};

const isUserAssignmentGroup = (group: any, members: any[]): boolean => {
    const type = group?.type ?? group?.assignmentType;
    return type === 'users' || type === 'user' || members.length > 0;
};

const resolveUserLabel = (userRef: any, usersById: Map<string, UserOption>): string => {
    if (!userRef) return '';

    if (typeof userRef === 'string') {
        const id = parseIdFromIri(userRef);
        if (!id) return '';
        const resolved = usersById.get(id);
        if (resolved) {
            return `${resolved.firstName} ${resolved.lastName}`.trim() || resolved.email || `User ${id}`;
        }
        return `User ${id}`;
    }

    if (typeof userRef === 'object') {
        const label =
            `${userRef.firstName || userRef.first_name || ''} ${userRef.lastName || userRef.last_name || ''}`.trim() ||
            userRef.email ||
            (userRef.id != null ? String(userRef.id) : '');
        if (label) return label;

        const id = userRef.id != null ? String(userRef.id) : parseIdFromIri(userRef['@id']);
        if (id) {
            const resolved = usersById.get(id);
            if (resolved) {
                return `${resolved.firstName} ${resolved.lastName}`.trim() || resolved.email || `User ${id}`;
            }
            return `User ${id}`;
        }
    }

    return '';
};

const resolveUserId = (userRef: any): string | null => {
    if (!userRef) return null;
    if (typeof userRef === 'string') return parseIdFromIri(userRef);
    if (typeof userRef === 'object') {
        if (userRef.id != null) return String(userRef.id);
        return parseIdFromIri(userRef['@id']);
    }
    return null;
};

/**
 * Builds a UserOption for a group member. Prefers the loaded users list, then any
 * name embedded in the group payload, so edit mode can prefill without waiting on users.
 */
const resolveMemberOption = (
    userRef: any,
    id: string,
    usersById: Map<string, UserOption>
): UserOption => {
    const loaded = usersById.get(id);
    if (loaded) return loaded;

    const embedded = typeof userRef === 'object' && userRef ? userRef : {};
    return {
        id: Number(id),
        firstName: embedded.firstName || embedded.first_name || '',
        lastName: embedded.lastName || embedded.last_name || '',
        email: embedded.email || '',
    };
};

export const mapChecklistAssignmentGroups = (
    rawGroups: any[],
    users: UserOption[] = []
): ChecklistAssignmentGroup[] => {
    const usersById = new Map(users.map((u) => [String(u.id), u]));

    return rawGroups.map((g) => {
        const members = extractGroupMembers(g);
        const assignmentType: AssignmentType = isUserAssignmentGroup(g, members) ? 'users' : 'security_role';

        const userLabels: string[] = [];
        const userIds: string[] = [];
        const selectedUsers: UserOption[] = [];

        members.forEach((member) => {
            const userRef = member?.user ?? member?.User ?? member;
            const id = resolveUserId(userRef);
            const label = resolveUserLabel(userRef, usersById);

            if (id && !userIds.includes(id)) {
                userIds.push(id);
                if (Number.isFinite(Number(id))) {
                    selectedUsers.push(resolveMemberOption(userRef, id, usersById));
                }
            }
            if (label && !userLabels.includes(label)) {
                userLabels.push(label);
            }
        });

        const securityRole = g.securityRole ?? g.security_role;
        let rolesAssignedLabel = '';
        let rolesAssignedId: number | null = null;

        if (securityRole) {
            if (typeof securityRole === 'object') {
                rolesAssignedLabel = securityRole.name || securityRole.label || '';
                rolesAssignedId = securityRole.id ?? null;
            } else if (typeof securityRole === 'string') {
                const roleId = parseIdFromIri(securityRole);
                rolesAssignedId = roleId ? Number(roleId) : null;
            }
        }

        return {
            id: g.id,
            groupName: g.label || g.name || 'Unnamed Group',
            assignmentType,
            rolesAssignedLabel,
            rolesAssignedId,
            userLabels,
            userIds,
            selectedUsers,
        };
    });
};
